// DXF price calculation logic
import { Group, Object3D } from "three";

interface DxfCostDetails {
  totalLength: number;
  cuttingTime: number; // in minutes
  materialCost: number;
  setupCost: number;
  powerCost: number;
  totalCost: number;
}

// Define cutting platforms and their properties
interface CuttingPlatform {
  name: string;
  costPerMinute: number;
  setupCost: number;
  powerCostPerMinute: number;
  cuttingSpeed: number; // units per minute
  minThickness: number;
  maxThickness: number;
}

const CUTTING_PLATFORMS: Record<string, CuttingPlatform> = {
  laser: {
    name: "Laser Cutter",
    costPerMinute: 0.5, // $/min
    setupCost: 10, // $
    powerCostPerMinute: 0.1, // $/min
    cuttingSpeed: 1000, // mm/min
    minThickness: 0.5,
    maxThickness: 20,
  },
  waterjet: {
    name: "Waterjet",
    costPerMinute: 1.0,
    setupCost: 15,
    powerCostPerMinute: 0.2,
    cuttingSpeed: 500,
    minThickness: 1,
    maxThickness: 100,
  },
  plasma: {
    name: "Plasma Cutter",
    costPerMinute: 0.8,
    setupCost: 12,
    powerCostPerMinute: 0.15,
    cuttingSpeed: 3000,
    minThickness: 1,
    maxThickness: 30,
  },
};

// Define materials and their costs
interface Material {
  name: string;
  costPerUnit: number; // $/mm or $/mm² depending on platform
  thickness: number; // mm
}

const MATERIALS: Record<string, Material> = {
  mild_steel: {
    name: "Mild Steel",
    costPerUnit: 0.05, // $/mm
    thickness: 3,
  },
  stainless_steel: {
    name: "Stainless Steel",
    costPerUnit: 0.12,
    thickness: 3,
  },
  aluminum: {
    name: "Aluminum",
    costPerUnit: 0.08,
    thickness: 3,
  },
  acrylic: {
    name: "Acrylic",
    costPerUnit: 0.03,
    thickness: 3,
  },
  wood: {
    name: "Wood",
    costPerUnit: 0.02,
    thickness: 6,
  },
};

function calculateObjectLength(object: Object3D): number {
  let totalLength = 0;
  let segmentCount = 0;

  object.traverse((child) => {
    // Check if this is a DXF line entity
    if (child.userData?.entity?.type === "LINE") {
      const start = child.userData.entity.start;
      const end = child.userData.entity.end;

      if (start && end) {
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);

        if (
          !isNaN(segmentLength) &&
          isFinite(segmentLength) &&
          segmentLength > 0
        ) {
          totalLength += segmentLength;
          segmentCount++;

          // Debug individual segments
          console.log("Line segment:", {
            index: segmentCount,
            start: [start.x, start.y],
            end: [end.x, end.y],
            length: segmentLength,
          });
        }
      }
    }
  });

  // For debugging
  console.log("Final calculation results:", {
    totalLength,
    segmentCount,
    scaledLength: totalLength,
    scaledLengthMM: totalLength.toFixed(2) + " mm",
  });

  // The coordinates are already in millimeters, no need to scale
  return Math.max(0, totalLength);
}

export function calculateDxfCosts(
  dxfObject: Group | null,
  platform: string = "laser",
  material: string = "mild_steel"
): DxfCostDetails {
  if (!dxfObject) {
    console.warn("No DXF object provided");
    return {
      totalLength: 0,
      cuttingTime: 0,
      materialCost: 0,
      setupCost: 0,
      powerCost: 0,
      totalCost: 0,
    };
  }

  const selectedPlatform =
    CUTTING_PLATFORMS[platform] || CUTTING_PLATFORMS.laser;
  const selectedMaterial = MATERIALS[material] || MATERIALS.mild_steel;

  // Calculate total path length and ensure it's not zero
  const totalLength = calculateObjectLength(dxfObject);
  console.log("Total path length:", totalLength, "mm");

  // Calculate cutting time with a minimum value
  const minCuttingTime = 1; // minimum 1 minute
  const calculatedCuttingTime = totalLength / selectedPlatform.cuttingSpeed;
  const cuttingTime = Math.max(minCuttingTime, calculatedCuttingTime);
  console.log("Cutting time:", cuttingTime, "minutes");

  // Calculate material cost based on length and material thickness
  const materialCost =
    totalLength * selectedMaterial.thickness * selectedMaterial.costPerUnit;

  // Calculate power cost
  const powerCost = cuttingTime * selectedPlatform.powerCostPerMinute;

  // Total cost calculation
  const totalCost =
    materialCost +
    selectedPlatform.setupCost +
    cuttingTime * selectedPlatform.costPerMinute +
    powerCost;

  const result = {
    totalLength,
    cuttingTime,
    materialCost,
    setupCost: selectedPlatform.setupCost,
    powerCost,
    totalCost,
  };

  console.log("Cost calculation result:", result);
  return result;
}

// Helper function to get available platforms
export function getAvailablePlatforms(): string[] {
  return Object.keys(CUTTING_PLATFORMS);
}

// Helper function to get available materials
export function getAvailableMaterials(): string[] {
  return Object.keys(MATERIALS);
}
